// Load Truck Simulator — 4-step wizard. Uses the same wizard infra as
// CreateNewProduct (WizardFormProvider + StepProgressBoard + WizardForm +
// WizardFormFooter) so the UX matches the rest of the app.
//
// Read-only simulator — no DB writes.

import React from 'react';
import { Card, Row, Col, Tab, Button } from 'react-bootstrap';

import useWizardForm from '../../hooks/useWizzardForm';
import WizardFormProvider from '../../providers/WizzardFormProvider';
import StepProgressBoard from '../../components/project-management/create-new/StepProgressBoard';
import WizardFormFooter from '../../components/project-management/create-new/WizardFormFooter';
import WizardForm from '../../components/project-management/create-new/WizzardForm';

import { TRUCK_PRESETS } from '../../features/warehouse/truckPacker.js';
import StepTruck from '../../components/warehouse/load-truck-wizard/StepTruck.js';
import StepItems from '../../components/warehouse/load-truck-wizard/StepItems.js';
import StepReview from '../../components/warehouse/load-truck-wizard/StepReview.js';
import StepSimulate from '../../components/warehouse/load-truck-wizard/StepSimulate.js';

const STEP_TITLES = ['Truck', 'Items', 'Review', 'Simulate'];
const LAST_IDX = STEP_TITLES.length - 1;  // 3
const DEFAULT_TRUCK = TRUCK_PRESETS.tautliner || Object.values(TRUCK_PRESETS)[0];

export default function LoadTruckSimulator() {
  // totalStep = LAST_IDX so getCanNextPage is false on the last step and the
  // footer submits instead of navigating past the end.
  const form = useWizardForm({ totalStep: LAST_IDX });
  const { selectedStep, startOver: resetWizard } = form;

  const [truck, setTruck] = React.useState({ ...DEFAULT_TRUCK, maxPayloadKg: 24000 });
  const [items, setItems] = React.useState([]);

  const startOver = () => {
    setTruck({ ...DEFAULT_TRUCK, maxPayloadKg: 24000 });
    setItems([]);
    resetWizard();
  };

  return (
    <WizardFormProvider {...form}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Load Truck Simulator</h4>
          <div className="text-body-secondary small">
            Pick a truck, build a load list from storage + production, review, then simulate in 3D.
          </div>
        </div>
        <Button variant="outline-danger" size="sm" onClick={startOver}>
          Reset
        </Button>
      </div>

      <StepProgressBoard className="mb-3" />

      <Row className="align-items-start">
        <Col xs={12}>
          <div className="theme-wizard d-flex flex-column">
            <Tab.Content>
              <Tab.Pane eventKey={0}>
                <WizardForm step={0}>
                  <StepCard title={`Step 1 · ${STEP_TITLES[0]}`}>
                    <StepTruck truck={truck} onChange={setTruck} />
                  </StepCard>
                </WizardForm>
              </Tab.Pane>

              <Tab.Pane eventKey={1}>
                <WizardForm step={1}>
                  <StepCard title={`Step 2 · ${STEP_TITLES[1]}`}>
                    <StepItems items={items} onChange={setItems} />
                  </StepCard>
                </WizardForm>
              </Tab.Pane>

              <Tab.Pane eventKey={2}>
                <WizardForm step={2}>
                  <StepCard title={`Step 3 · ${STEP_TITLES[2]}`}>
                    <StepReview truck={truck} items={items} />
                  </StepCard>
                </WizardForm>
              </Tab.Pane>

              <Tab.Pane eventKey={3}>
                <WizardForm step={3}>
                  <StepCard title={`Step 4 · ${STEP_TITLES[3]}`}>
                    <StepSimulate truck={truck} items={items} />
                  </StepCard>
                </WizardForm>
              </Tab.Pane>
            </Tab.Content>

            <WizardFormFooter
              nextBtnLabel={selectedStep === LAST_IDX ? 'Start over' : 'Next'}
              handleSubmit={startOver}
              className="mb-4"
            />
          </div>
        </Col>
      </Row>
    </WizardFormProvider>
  );
}

function StepCard({ title, children }) {
  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="fw-bold">{title}</Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
}
